import React, { useCallback, useContext, useState } from "react";
import { Button, Container, Drawer, Flex, Input, Select, Text } from "@cisco/spaces-dash-lib-styleguide";
import { GoogleMap, Marker } from "@react-google-maps/api";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { I18nContext } from "../../contexts/i18nContext";
import MapPin from "../../assets/images/map/MapPin2.svg";

function LocationEditPopup(props) {
  const i18n = useContext(I18nContext);
  const { t } = i18n;
  const { showEditPopup, cancelEdit, saveLocation, editingNode, setEditingNode, validateTotalArea, unitList, canShowSection, node, getZipCode, timeZoneList } = props;

  const OPTIONS = {
    minZoom: 2,
    maxZoom: 18
  };

  const [zoom, setZoom] = useState(2);

  const onLoad = useCallback(() => {
    setTimeout(() => {
      if (editingNode.latitude && editingNode.longitude) {
        setZoom(12);
      }
    }, 300);
  }, [editingNode.latitude, editingNode.longitude]);

  const onDragEnd = (e) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    setEditingNode({ ...editingNode, latitude: lat, longitude: lng });
  };

  const updateNode = (key, value) => {
    if (!key) {
      throw new Error("Key is required to update the node");
    }
    const newEditingNode = { ...editingNode };
    newEditingNode[key] = value?.toString();
    setEditingNode(newEditingNode);
  };

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const latLng = await getLatLng(results[0]);
    setEditingNode((prev) => ({
      ...prev,
      latitude: latLng?.lat,
      longitude: latLng?.lng,
      address: value
    }));
    setZoom(12);
  };

  const setAddress = (value) => {
    updateNode("address", value);
  };

  return (
    <Drawer size={canShowSection() ? "lg" : "md"} isOpen={showEditPopup}>
      <Drawer.Heading>{i18n.t("location-info.edit-details-text")}</Drawer.Heading>
      <Drawer.Content>
        <Flex gap={16}>
          <Flex style={{ width: !canShowSection() ? "100%" : "auto" }} direction="vertical" gap={16}>
            <Input disabled label={t("hierarchy-nodes.location-name-text")} placeholder={editingNode?.name} />
            <Input
              label={t("hierarchy-nodes.brand-text")}
              placeholder={t("hierarchy-nodes.enter-brand-name-text")}
              value={editingNode?.brandName}
              onChange={(e) => updateNode("brandName", e.target.value)}
            />
            <Input
              label={t("hierarchy-nodes.total-area-text")}
              placeholder={t("hierarchy-nodes.enter-total-area-text")}
              value={editingNode?.totalAreaValue}
              onChange={(e) => {
                updateNode("totalAreaValue", e.target.value);
                validateTotalArea();
              }}
            />
            <div>
              <Text weight="semi-bold" style={{ marginBottom: "4px" }}>
                {t("hierarchy-nodes.unit-text")}
              </Text>
              <Select
                fixedWidth
                clearable={false}
                options={unitList}
                placeholder={t("hierarchy-nodes.select-unit-text")}
                value={editingNode?.totalAreaUnit}
                onChange={(e) => {
                  updateNode("totalAreaUnit", e);
                  validateTotalArea();
                }}
              />
            </div>
            <Input.Number
              label={t("hierarchy-nodes.occupancy-limit-max")}
              placeholder={t("hierarchy-nodes.enter-occupancy-limit")}
              value={editingNode?.totalCapacity}
              onChange={(e) => updateNode("totalCapacity", e.target.value)}
            />
            {canShowSection() && (
              <Flex direction="vertical" gap={16}>
                {PlacesAutocomplete && (
                  <PlacesAutocomplete
                    label={t("hierarchy-nodes.address-text")}
                    placeholder={t("hierarchy-nodes.enter-address-text")}
                    value={editingNode?.address || ""}
                    onChange={setAddress}
                    onSelect={handleSelect}
                  >
                    {({ getInputProps, suggestions, getSuggestionItemProps }) => {
                      return (
                        <div>
                          <Input {...getInputProps()} label={t("hierarchy-nodes.address-text")} placeholder={t("hierarchy-nodes.enter-address-text")} />
                          {suggestions.length > 0 && (
                            <Container
                              style={{
                                maxHeight: "240px",
                                maxWidth: "360px",
                                overflowY: "scroll",
                                position: "absolute",
                                zIndex: 1000,
                                marginTop: "8px"
                              }}
                            >
                              <Flex direction="vertical">
                                {suggestions.map((suggestion) => {
                                  return (
                                    <Flex
                                      style={{
                                        borderRadius: "8px",
                                        cursor: suggestion.active ? "pointer" : "default",
                                        background: suggestion.active ? "#cce1ff" : "#ffffff"
                                      }}
                                      key={suggestion.placeId}
                                    >
                                      <Text size="p2" style={{ padding: "8px 12px" }} {...getSuggestionItemProps(suggestion)}>
                                        {suggestion.description}
                                      </Text>
                                    </Flex>
                                  );
                                })}
                              </Flex>
                            </Container>
                          )}
                        </div>
                      );
                    }}
                  </PlacesAutocomplete>
                )}
                <Flex gap={16}>
                  <Input
                    className="loc-info-edit-flex-grow"
                    size="md"
                    style={{ flexGrow: 1 }}
                    label={t("hierarchy-nodes.latitude-text")}
                    placeholder={t("hierarchy-nodes.enter-latitude-text")}
                    value={editingNode?.latitude}
                    onChange={(e) => updateNode("latitude", e.target.value)}
                  />
                  <Input
                    className="loc-info-edit-flex-grow"
                    size="md"
                    label={t("hierarchy-nodes.longitude-text")}
                    placeholder={t("hierarchy-nodes.enter-longitude-text")}
                    value={editingNode?.longitude}
                    onChange={(e) => updateNode("longitude", e.target.value)}
                  />
                </Flex>
              </Flex>
            )}
            {node?.data && (
              <Flex direction="vertical" gap={16}>
                {node?.data?.country && <Input disabled label={t("hierarchy-nodes.country-normal-text")} placeholder={node?.data?.country} />}
                {node?.data?.state && <Input disabled label={t("hierarchy-nodes.state-normal-text")} placeholder={node?.data?.state} />}
                {node?.data?.city && <Input disabled label={t("hierarchy-nodes.city-normal-text")} placeholder={node?.data?.city} />}
                {getZipCode() && <Input disabled label={t("hierarchy-nodes.zipcode-normal-text")} placeholder={getZipCode()} />}
              </Flex>
            )}
            <Flex direction="vertical" gap={4}>
              <Text style={{ fontWeight: 500 }}>{t("hierarchy-nodes.select-timezone-text")}</Text>
              <Select
                clearable={false}
                searchable
                placeholder={t("hierarchy-nodes.select-timezone-text")}
                // iconOnlyError={!editingNode.timeZoneId}
                fixedWidth
                options={timeZoneList}
                value={editingNode.timeZoneId}
                onChange={(e) => {
                  updateNode("timeZoneId", e);
                }}
              />
            </Flex>
          </Flex>
          {canShowSection() && (
            <Flex grow>
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                zoom={zoom}
                options={OPTIONS}
                center={{
                  lat: parseFloat(editingNode.latitude || 0),
                  lng: parseFloat(editingNode.longitude || 0)
                }}
                onLoad={onLoad}
              >
                {editingNode.latitude && editingNode.longitude && (
                  <Marker
                    position={{
                      lat: parseFloat(editingNode.latitude),
                      lng: parseFloat(editingNode.longitude)
                    }}
                    icon={MapPin}
                    draggable
                    onDragEnd={onDragEnd}
                  />
                )}
              </GoogleMap>
            </Flex>
          )}
        </Flex>
      </Drawer.Content>
      <Drawer.Footer>
        <Flex style={{ width: "100%" }} justify="flex-end">
          <Button.Group>
            <Button kind="tertiary" onClick={cancelEdit} id="location-cancel-button">
              {i18n.t("business-location-details.cancel-text")}
            </Button>
            <Button onClick={saveLocation} id="location-save-button">
              {i18n.t("hierarchy-nodes.save-text")}
            </Button>
          </Button.Group>
        </Flex>
      </Drawer.Footer>
    </Drawer>
  );
}

export default LocationEditPopup;
